// Profile bottom sheet for mobile - unified with new BottomSheet component
// UPDATED: Simplified to use unified BottomSheet with consistent behavior
// Features: Avatar, user info, password change, logout
// Uses React Portal to render outside DOM hierarchy, preventing z-index stacking context issues
// Optimized touch targets (min 44-48px)

import { KeyRound, LogOut } from 'lucide-react';
import { getRoleTheme, type UserRole } from '../../lib/roleThemes';
import { BottomSheet } from '../ui/BottomSheet';
import RoleBadge from '../ui/RoleBadge';

interface ProfileBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  userInitials: string;
  userRole: UserRole;
  onPasswordChange: () => void;
  onLogout: () => void;
}

export function ProfileBottomSheet({
  isOpen,
  onClose,
  userName,
  userInitials,
  userRole,
  onPasswordChange,
  onLogout
}: ProfileBottomSheetProps) {
  const theme = getRoleTheme(userRole);

  const handlePasswordChange = () => {
    onClose();
    setTimeout(() => {
      onPasswordChange();
    }, 300);
  };

  const handleLogout = () => {
    onClose();
    setTimeout(() => {
      onLogout();
    }, 300);
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Mon profil"
    >
      {/* User Info */}
      <div className="px-6 py-6 space-y-6">
        <div className="flex items-center gap-4">
          <div
            className={`w-16 h-16 rounded-full bg-gradient-to-br ${theme.avatarGradient} flex items-center justify-center text-white font-bold text-xl flex-shrink-0`}
          >
            {userInitials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-lg font-semibold text-gray-900 truncate">{userName}</p>
            <div className="mt-1">
              <RoleBadge role={userRole} size="md" />
            </div>
          </div>
        </div>

        {/* Actions - Optimized touch targets */}
        <div className="space-y-2">
          <button
            onClick={handlePasswordChange}
            className="w-full flex items-center gap-3 px-4 py-4 min-h-[48px] text-gray-700 hover:bg-gray-50 rounded-lg transition-colors border border-gray-200"
          >
            <KeyRound size={20} className="text-gray-500 flex-shrink-0" />
            <span className="font-medium text-sm">Changer le mot de passe</span>
          </button>

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-4 min-h-[48px] text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
          >
            <LogOut size={20} className="flex-shrink-0" />
            <span className="font-medium text-sm">Déconnexion</span>
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
